import React from 'react';
import {SynchronousPromise} from 'synchronous-promise';

import {DOCUMENT_STATE} from 'front-onceforall-core/dist/utils/documents';
import {COUNTRY} from 'front-onceforall-core/dist/utils/country';
import {REGISTRATION_NUMBER_CODE, VAT_IDENTIFICATION_NUMBER_CODE} from 'front-onceforall-core/dist/utils/organisations';
import {aVATIdentificationNumber, anIdentificationNumberMap, aDocumentType} from 'front-onceforall-core/dist/testUtils/testDocumentUtils';
import {DOSSIER_STATE} from 'front-onceforall-core/dist/utils/dossiers';

import authenticationService from '../services/authentication/AuthenticationService';
import {
  USER_OPTIONS,
  BACKOFFICE_ROLE,
  CONSULTANT_ROLE,
  EXPLORER_ROLE,
  interventionRole,
  INVITING_ROLE,
  MANAGE_PROJECT_PERMISSION,
  MANAGER_ROLE,
  notificationStatus,
  PROJECT_STATUS,
  projectType,
  REJECTION_REASONS,
  RELATIONSHIP_TYPE,
  status,
  notificationType
} from './constant';
import {keep} from './objects';
import {getTabItems} from '../components/clientDetails/Tabs/utils';

export const anEmptyFunction = () => () => { /* voluntarily empty */ };

// https://github.com/facebook/jest/issues/2157#issuecomment-279171856
export const flushPromises = () => new Promise(resolve => setTimeout(resolve, 0));

export const mockedUseEffect = () => {
  jest.spyOn(React, 'useEffect').mockImplementationOnce(f => f());
};

// ---------------------------- ADDRESS ----------------------------

export const anAddress = custom => ({addressLine: '50, rue des oiseaux',
  city: 'Lyon',
  postCode: '69432',
  region: 'Auvergne Rhone Alpes',
  ...custom});

// ---------------------------- USER ----------------------------

export const aUser = custom => ({id: '2dff3852-ee1c-40d5-a2d0-b5d7d5619169',
  email: 'firstName.lastName@mail.com',
  emailVerified: true,
  password: 'aaaaaaA1',
  firstName: 'FirstName',
  lastName: 'LastName',
  fullName: 'FirstName LastName',
  language: 'en',
  phoneNumber: '+33412345678',
  mobilePhoneNumber: '+33612345678',
  position: 'CEO',
  createdDate: '2018-03-28T15:00:24.799Z',
  organisationIds: ['1'],
  enabled: true,
  deactivatedDate: null,
  organisationRole: INVITING_ROLE,
  roleByOrganisationIds: {
    1: INVITING_ROLE
  },
  organisationPermissions: [],
  permissionsByOrganisationIds: {
    1: []
  },
  userOrganisations: [],
  options: [USER_OPTIONS.SUGGESTION_DISPLAY],
  ...custom});

export const aUserRegistration = custom => ({
  ...aUser(),
  emailConfirmation: 'firstName.lastName@mail.com',
  passwordConfirmation: 'aaaaaaA1',
  termsAccepted: true,
  privacyPolicyAccepted: true,
  ...custom});

export const aTextToValidate = custom => ({name: 'termsAndConditions',
  timestampName: 'termsAndConditionsTimestamp',
  lastUpdateMethod: () => 1566834504,
  acceptMethod: anEmptyFunction(),
  ...custom});

// ---------------------------- ORGANISATION ----------------------------

export const aRegistrationNumberType = custom => ({code: REGISTRATION_NUMBER_CODE,
  country: COUNTRY.GERMANY,
  format: '^\\d{8}$',
  required: false,
  ...custom});

export const anIdentificationNumberType = custom => ({code: VAT_IDENTIFICATION_NUMBER_CODE,
  country: COUNTRY.GERMANY,
  format: '^DE\\d{9}$',
  required: false,
  ...custom});

export const anOrganisation = custom => ({organisationId: 1,
  country: COUNTRY.GERMANY,
  fullName: 'Organisation 1',
  registrationNumber: '12345678',
  businessLine: 'CONSTRUCTION',
  accessCode: '',
  address: anAddress(),
  identificationNumbers: anIdentificationNumberMap(aVATIdentificationNumber('DE123456789')),
  enabled: true,
  deactivationReason: null,
  remote: false,
  groupId: 1,
  depositor: true,
  planSelected: false,
  website: 'https://www.example.com',
  ...custom});

export const aDetailedOrganisation = custom => ({organisationId: 1,
  country: COUNTRY.GERMANY,
  fullName: 'Organisation 1',
  registrationNumber: '12345678',
  businessLine: 'CONSTRUCTION',
  address: anAddress(),
  identificationNumbers: anIdentificationNumberMap(aVATIdentificationNumber('DE123456789')),
  depositor: true,
  mandateService: true,
  registrationDate: '2017-01-15T17:00:15Z',
  enabled: true,
  deactivationReason: null,
  priorAcceptation: true,
  website: 'https://www.example.com',
  ...custom});

// ---------------------------- RELATIONSHIP ----------------------------

export const aRelationship = custom => ({relationshipId: 12,
  subcontractor: anOrganisation({organisationId: 2}),
  relationshipType: RELATIONSHIP_TYPE.SUB_CONTRACTOR_OF,
  client: anOrganisation(),
  status: status.ACCEPTED,
  emitterOrganisationId: anOrganisation().organisationId,
  creationDate: '2018-07-15T17:00:15Z',
  resendable: true,
  favorite: false,
  ...custom});

export const aFavoriteRelationship = custom => ({id: 500,
  relationshipId: 12,
  userId: 'uid',
  ...custom});

// ---------------------------- PROJECT ----------------------------

export const aProject = custom => ({projectId: 12,
  name: 'Incity',
  internalReference: 'ref1234',
  address: anAddress(),
  country: COUNTRY.SPAIN,
  openingDate: '2017-01-21T00:00:00.000+02:00',
  provisionalAcceptanceDate: '2017-10-21T23:59:59.000+02:00',
  additionalNotes: 'Additional notes',
  creationDate: '2017-01-15T17:00:15Z',
  ownerOrganisation: anOrganisation(),
  projectStatus: PROJECT_STATUS.ACTIVE,
  closingDate: null,
  projectType: projectType.STANDARD,
  ...custom});

export const anInternalNote = custom => ({id: 1,
  firstName: 'John',
  lastName: 'Doe',
  creationDate: '2019-01-15T17:00:15Z',
  contentNote: 'This an internal note text. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
  ...custom});

export const anIntervention = custom => ({
  creationDate: '2019-01-11T17:00:15Z',
  lastSentDate: '2019-01-11T17:00:15Z',
  interventionDate: null,
  depth: 0,
  interventionId: 75,
  project: aProject(),
  organisation: anOrganisation(),
  role: interventionRole.GENERAL_CONTRACTOR,
  interventionPath: '1',
  invitationEmitterOrganisation: anOrganisation(),
  status: status.ACCEPTED,
  dossierStateAccessible: true,
  dossierDocumentsAccessible: true,
  ...custom});

export const aProjectWithOneIntervention = custom => ({...aProject({projectId: 14}),
  interventions: [
    {
      id: 431,
      role: interventionRole.GENERAL_CONTRACTOR,
      invitingOrganisation: null
    }
  ],
  ...custom});

export const aProjectWithMultipleInterventions = custom => ({...aProject({projectId: 15}),
  interventions: [
    {
      id: 432,
      role: interventionRole.GENERAL_CONTRACTOR,
      invitingOrganisation: null
    },
    {
      id: 433,
      role: interventionRole.SUB_CONTRACTOR,
      invitingOrganisation: {
        ...anOrganisation({organisationId: 81, fullName: 'Test orga'})
      }
    }
  ],
  ...custom});

export const aSettings = custom => ({organisationId: 1,
  priorAcceptation: false,
  ...custom});

export const aProjectParticipant = custom => ({role: interventionRole.GENERAL_CONTRACTOR,
  count: 10,
  ...custom});

export const aProjectUserPermission = custom => ({organisationId: 5,
  projectId: 2,
  user: {
    userId: '8b081608-b225-4a39-a150-71e958f2596d',
    fullName: 'Jorge Doe',
    organisationRole: MANAGER_ROLE,
    organisationPermissions: [MANAGE_PROJECT_PERMISSION]
  },
  viewProject: false,
  ...custom});
// ---------------------------- DOCUMENT ----------------------------

export const aLegalDossier = custom => ({type: 'LEGAL', ...custom});
export const anAdditionalDossier = custom => ({type: 'ADDITIONAL', ...custom});
export const aBusinessDossier = custom => ({type: 'BUSINESS',
  business: 'businessName',
  ...custom});
export const aProjectDossier = custom => ({type: 'PROJECT',
  project: {
    name: 'Project name'
  },
  ...custom});
export const aRelationshipDossier = custom => ({type: 'RELATIONSHIP',
  client: {
    fullName: 'Relationship name'
  },
  ...custom});

export const aDocumentSubtype = custom => ({code: 'REG_BUSINESS',
  documentTypeId: 3,
  name: 'Gewerbeanmeldung',
  parentCode: 'REGISTRATION',
  parentName: 'Registration document',
  multiOccurrence: false,
  validityPeriod: 12,
  designationMandatory: false,
  dossier: 'BUSINESS',
  country: COUNTRY.GERMANY,
  abstractType: false,
  ...custom});

/**
 * PLATINT-4114: separating files from documents
 * Serves to migrate slowly to lighter API responses from api-ecm
 */
export const aDocumentMetadata = custom => ({uid: 'f81d4fae-7dec-11d0-a765-00a0c91e6bf7',
  correlationId: 'f81d4fae-7dec-11d0-a765-00a0c91e6fff',
  state: DOCUMENT_STATE.MISSING,
  type: aDocumentSubtype(),
  validityStart: '2018-01-01T08:41:32.957Z',
  validityEnd: '2018-03-01T08:41:32.957Z',
  userId: aUser().id,
  dossiers: [aLegalDossier()],
  rejectionReason: null,
  additionalDesignation: 'First certificate',
  almostExpired: false,
  groupLevel: false,
  organisations: [],
  gatheredByDepositor: false,
  collectionMandateEnabled: false,
  toSignOnline: false,
  unpublished: false,
  overallStatusDocument: true,
  projectContext: [],
  ...custom});

export const aDocument = custom => ({
  ...aDocumentMetadata(),
  hasFile: true,
  hasExcelFile: false,
  ...custom});

export const aLegalDocument = custom => Object.assign(aDocument(), {
  type: aDocumentSubtype({dossier: 'LEGAL'}),
  dossiers: [aLegalDossier()]
}, custom);

export const aBusinessDocument = custom => Object.assign(aDocument(), {
  type: aDocumentSubtype({dossier: 'BUSINESS'}),
  dossiers: [aBusinessDossier()]
}, custom);

export const anAdditionalDocument = custom => Object.assign(aDocument(), {
  type: aDocumentSubtype({dossier: 'ADDITIONAL'}),
  dossiers: [anAdditionalDossier()]
}, custom);

export const aProjectDocument = custom => Object.assign(aDocument(), {
  type: aDocumentSubtype({dossier: 'PROJECT'}),
  dossiers: [aProjectDossier()]
}, custom);

export const aFile = custom => ({lastModified: '1520333223000',
  lastModifiedDate: 'Date 2018-03-06T10:47:03.000Z',
  name: 'presentation-RGPD.pdf',
  preview: 'blob:http://localhost:3000/8e457430-d482-42f3-9829-3ff80e85afe3',
  size: 9983705,
  type: 'application/pdf',
  webkitRelativePath: '',
  ...custom});

export const aDossierCountPerState = custom => ({dossierState: 'VALID',
  count: 1,
  ...custom});

export const aDocumentCountPerState = custom => ({documentState: 'VALID',
  count: 1,
  ...custom});

export const anInvalidOrganisationDocumentsObject = custom => ({organisation: anOrganisation(),
  documents: [aDocument()],
  ...custom});

export const anInvalidOrganisationDocumentsWithInterventionObject = custom => ({organisation: anOrganisation(),
  interventionId: 75,
  documents: [aDocument()],
  ...custom});

// ------------------------------- REMINDER -----------------------------

export const aReminder = custom => ({id: 13401,
  remindedOrganisationId: 1,
  remindedOrganisationName: 'Organisation 1',
  projectId: 56416,
  projectName: 'Chantier du parc',
  reason: 'CONTRACT',
  additionalContext: 'Lorem ipsum dolor sit amet',
  remindDate: '2019-08-26T15:50:40Z',
  status: 'OPEN',
  documents: [
    {
      correlationId: aDocument().correlationId,
      documentState: aDocument().state,
      uid: aDocument().uid
    }
  ],
  ...custom});

// ------------------------------- MANDATE ------------------------------

export const aCollectionMandate = custom => ({mandateId: 1,
  documentType: {
    code: 'INS_RC_PRO',
    documentTypeId: 53,
    name: 'Assurance RC Professionnelle'
  },
  fields: {
    ORGANISATION: 'Assurance Pro.',
    CONTRACT_NUMBER: 'ABC010203',
    EMAIL: 'test@onceforall.com',
    PHONE_NUMBER: ''
  },
  ...custom});

// ---------------------------- SUBSCRIPTION ----------------------------

export const aUserSubscription = custom => ({userId: '2dff3852-ee1c-40d5-a2d0-b5d7d5619169',
  organisationIds: [1],
  email: 'firstName.lastName@mail.com',
  subscriptions: ['COLLECTOR_PERIODIC_PROJECT', 'COLLECTOR_EVENT'],
  ...custom});

export const aSubscription = custom => ({externalId: '876hhtr3-8hgf-085f-13s5-6h9kufgx754d',
  planId: 'fr_artisan_1y_v1',
  maximumNumberOfStandardProjects: 5,
  maximumNumberOfNominativeProjects: 4,
  maximumNumberOfUserAccounts: 6,
  country: COUNTRY.FRANCE,
  scope: 'GROUP',
  referenceId: '123456789',
  depositorServiceAuthorized: true,
  masterPlatform: null,
  numberOfStandardProjects: 2,
  numberOfNominativeProjects: 3,
  numberOfUserAccounts: 4,
  ...custom});

export const anOption = custom => ({
  id: 'fr_additional_user_v1',
  quantity: 1,
  price: 6000,
  priority: 300,
  translatedLabel: 'Additional users',
  unit: 'user',
  unitPeriod: 'year',
  ...custom
});

export const aFeature = custom => ({
  id: 'featureId-1',
  translatedLabel: 'awesome feature',
  ...custom
});

export const aPlan = custom => ({
  externalId: 'fr_serenity_1y_v2',
  currency: 'EUR',
  pricePeriod: 'month',
  price: 2990, /* Always an integer value (cents of the currency) */
  setupFee: 990, /* Always an integer value (cents of the currency) */
  targetSystems: 'ofa',
  upgradable: true,
  depositor: true,
  services: [{
    id: 'fr_depositor_v1',
    priority: -1, /* Priority is always an integer value, the highest the priority, the item shall be displayed at the top. */
    quantity: 1,
    price: null,
    unit: null,
    unitPeriod: null
  }, {
    id: 'fr_included_user_v1',
    priority: 1000, /* Priority is always an integer value, the highest the priority, the item shall be displayed at the top. */
    quantity: 6, /* optional */
    price: null,
    unit: 'user',
    unitPeriod: null
  }, {
    id: 'fr_kbis_purchase_v1',
    priority: 900,
    quantity: null,
    price: null,
    unit: null,
    unitPeriod: 'month'
  }, {
    id: 'fr_included_collection_mandate_v1',
    priority: 800,
    quantity: 2,
    price: 500,
    unit: null,
    unitPeriod: 'month'
  }, {
    id: 'fr_additional_user_v1',
    priority: 1100,
    quantity: 1,
    price: 500,
    unit: 'user',
    unitPeriod: 'month'
  }, {
    id: 'fr_opera_option_v1',
    priority: 500,
    quantity: 1,
    price: 500,
    unit: null,
    unitPeriod: 'month'
  }],
  ...custom});

export const anOrganisationDossierStats = custom => ({organisationId: 1,
  country: COUNTRY.FRANCE,
  validityPercentage: 27,
  documentStateCount: {
    total: 27,
    VALID: 4,
    EXPIRED: 5,
    ALMOST_EXPIRED: 2,
    MISSING: 6,
    REJECTED: 3,
    SIGNED: 1,
    AWAITING_SIGNATURE: 0,
    AWAITING_VALIDATION: 3,
    AWAITING_GATHERING: 4,
    NOT_APPLICABLE: 0
  },
  ...custom});

export const aDossierStats = custom => ({organisations: [
  anOrganisationDossierStats(),
  anOrganisationDossierStats({
    organisationId: 6,
    country: COUNTRY.GERMANY,
    validityPercentage: 100,
    documentStateCount: {
      total: 21,
      VALID: 15,
      EXPIRED: 0,
      ALMOST_EXPIRED: 2,
      MISSING: 0,
      REJECTED: 0,
      SIGNED: 4,
      AWAITING_SIGNATURE: 0,
      AWAITING_VALIDATION: 0,
      AWAITING_GATHERING: 0,
      NOT_APPLICABLE: 0
    }
  })
],
lastUpdateDate: 1573585720917,
...custom});

export const anInvoice = custom => ({id: 'FR01IN-519',
  status: 'PAYMENT_DUE',
  date: '2020-03-05T23:00:00Z',
  dueDate: '2020-04-05T23:00:00Z',
  paymentDate: '2020-03-09T09:36:46Z',
  totalAmount: 46440,
  dueAmount: 1000,
  currency: 'EUR',
  billingAddress: {
    firstName: 'Hola',
    lastName: 'Stéphane',
    company: 'MATHILDE B',
    line1: '16 Quai de la Marne',
    city: 'PARIS 19',
    country: 'FR',
    zip: '75019'
  },
  downloadUrl: 'url',
  ...custom});

// ---------------------------- ENROLLMENT ----------------------------

export const anInvitation = custom => ({
  invitationId: 49,
  organisationSourceId: 2,
  organisationName: 'New org',
  email: 'firstName.lastName@mail.com',
  firstName: 'First name',
  lastName: 'Last name',
  customMessage: 'Invitation message',
  language: 'fr',
  creationDate: '2018-02-01T08:41:32.957Z',
  lastSentDate: '2018-03-01T08:41:32.957Z',
  resendable: true,
  phoneNumber: '+33 1 33 34 50 02',
  country: COUNTRY.FRANCE,
  registrationNumber: '47791069900016',
  vatIdentificationNumber: 'FR38477999999',
  status: status.PENDING,
  ...custom});

export const anInvitationFromToken = custom => keep(
  anInvitation(custom),
  ['invitationFromToken', 'email', 'firstName', 'lastName', 'phoneNumber']
);

export const anInvitationFromAPI = custom => ({
  id: 49,
  invitingUserId: 'ab123-cd456-ef789-gh753',
  organisationSourceId: 456789,
  emitterOrganisationId: 897654,
  invitedContactEmail: 'firstName.lastName@mail.com',
  invitedOrganisationName: 'ALG',
  invitedContactFirstName: 'First name',
  invitedContactLastName: 'Last name',
  expirationDate: '1970-01-01T12:59:00Z',
  invitedContactLanguage: 'fr',
  invitedContactPhoneNumber: '+33 1 33 34 50 02',
  valid: true,
  status: 'PENDING',
  project: {
    projectId: 456123,
    sourceRole: 'CLIENT',
    targetRole: 'SERVICE_PROVIDER',
    projectName: 'Empire State Building'
  },
  invitationType: 'PROJECT',
  ...custom
});

export const anAccessCodeInvitation = custom => Object.assign(anInvitation(), {
  email: null,
  firstName: null,
  lastName: null,
  registrationNumber: null,
  vatIdentificationNumber: null,
  accessCode: 'KuTUu7I9hs20',
  tenderReference: 'Some tender reference'
}, custom);

// --------------------------- NOTIFICATION ---------------------------

export const aDocumentOfNotification = custom => ({documentId: 'f81d4fae-7dec-11d0-a765-00a0c91e6bf8',
  documentName: 'Gewerbeanmeldung',
  previousDocumentName: 'Previous name',
  rejectionReason: 'REJECTION REASON',
  expirationDate: '2018-11-25T10:15:30.00Z',
  certifyingThirdParty: 'CDS Lyon',
  ...custom});

export const aRelationshipOfNotification = custom => ({relationshipId: 1,
  organisationSourceId: 10,
  relationshipType: RELATIONSHIP_TYPE.CLIENT_OF,
  invitingOrganisationName: 'INVITING_CLIENT',
  invitedOrganisationName: 'INVITED_ORG',
  rejectionReason: REJECTION_REASONS.UNKNOWN_ORGANISATION,
  ...custom});

export const anInterventionOfNotification = custom => ({interventionId: 1,
  projectId: 1,
  projectName: 'PROJECT_NAME',
  invitingOrganisationName: 'INVITING_CLIENT',
  invitedOrganisationName: 'INVITED_ORG',
  rejectionReason: REJECTION_REASONS.UNKNOWN_ORGANISATION,
  ...custom});

export const aProjectOfNotification = custom => ({projectName: 'PROJECT_NAME',
  organisationOwnerName: 'ALG',
  ...custom});

export const anUserOfNotification = custom => ({email: 'johndoe@yopmail.com',
  firstName: 'John',
  lastName: 'Doe',
  role: 'DEFAULT',
  organisationName: 'ALG',
  userId: '761ef061-6299-4992-ae16-550f0222072e',
  ...custom});

export const aNotification = custom => ({notificationId: 1,
  organisationId: 1,
  creationDate: '2018-11-20T10:15:30.00Z',
  status: notificationStatus.NEW,
  document: aDocumentOfNotification(),
  relationship: aRelationshipOfNotification(),
  intervention: anInterventionOfNotification(),
  project: aProjectOfNotification(),
  user: anUserOfNotification(),
  documentAtDate: {
    documentName: 'Attestation Décennale Standard',
    year: 2019,
    projects: [
      {id: 1252, name: 'Projet Nominatif', openingDate: '2019-11-30T23:00:00Z'},
      {id: 1402, name: 'HCORP Project', openingDate: '2019-11-30T23:00:00Z'},
      {id: 1852, name: 'Finale Opéra', openingDate: '2019-11-30T23:00:00Z'},
      {id: 1101, name: 'HCORP gc project', openingDate: '2019-11-30T23:00:00Z'}
    ]
  },
  ...custom});

export const aNotificationWithActions = custom => ({...aNotification(), type: notificationType.RELATIONSHIP_NOT_AUTOMATICALLY_ACCEPTED, ...custom});

export const aNotificationWithoutAction = custom => ({...aNotification(), type: notificationType.DOCUMENT_VALIDATED, ...custom});

export const aNotificationsState = custom => ({

  isFetching: false,
  informationNotifications: {
    notifications: [aNotificationWithoutAction({notificationId: 2})],
    numberOfNewNotifications: 1,
    totalCount: 1
  },
  notificationsWithActions: [aNotificationWithActions()],
  organisationId: null,
  ...custom});

export const aSuggestionInvitation = custom => ({
  type: notificationType.RELATIONSHIP_SUGGESTED,
  status: notificationStatus.NEW,
  viewed: false,
  invitationId: 3,
  organisationId: 1,
  registrationNumber: '1245678945612',
  identificationNumbers: {
    VAT_IDENTIFICATION_NUMBER: 'FR12345678912345'
  },
  invitingOrganisationName: 'ALG',
  country: 'FRANCE',
  address: {
    addressLine: '1 rue de la paix',
    city: 'LYON',
    postCode: '69003'
  },
  ...custom
});

export const aRequestType = custom => ({recordTypeId: '0121t000000Lp19AAC',
  label: 'Mon offre',
  value: 'Mon offre',
  subtypes: [{
    label: 'Autres demandes',
    value: 'Autres demandes'
  },
  {
    label: 'Ma facture',
    value: 'Ma facture'
  },
  {
    label: 'La résiliation de mon abonnement',
    value: 'La résiliation de mon abonnement'
  },
  {
    label: 'Souscrire une option supplémentaire (opera, mandat de diffusion, utilisateurs)',
    value: 'Souscrire une option supplémentaire (opera, mandat de diffusion, utilisateurs)'
  }],
  ...custom});

// ---------------------- INFORMATION MESSAGE ----------------------

export const anInformationMessage = custom => ({fr: 'C\'est un message de test',
  de: 'Dies ist eine Testmeldung',
  en: 'This is a test message',
  ...custom});

// ----------------------------- TOOLS -----------------------------

export const mockedEvent = (value = undefined, custom = {}) => ({
  preventDefault: jest.fn(),
  stopPropagation: jest.fn(),
  target: {
    value
  },
  ...custom
});

// ---------------------------- RESPONSE ----------------------------

const httpError = (statusCode, jsonBody = {}) => (SynchronousPromise.reject({
  response: {
    status: statusCode,
    json: (() => SynchronousPromise.resolve(jsonBody))
  }
}));

export const anHttpOk = (jsonBody = {}) => SynchronousPromise.resolve(jsonBody);

export const anHttpError = (statusCode = 500, jsonBody = {}) => httpError(statusCode, jsonBody);

export const a404HttpError = (statusCode = 404, jsonBody = {}) => httpError(statusCode, jsonBody);

export const a400HttpError = (statusCode = 400, jsonBody = {}) => httpError(statusCode, jsonBody);

export const anAbortSignal = () => new AbortController().signal;

export const aLocationProp = custom => ({pathname: '/',
  search: '',
  ...custom});

export const anHistory = () => ({
  push: jest.fn(),
  goBack: jest.fn(),
  replace: jest.fn()
});

const mockUserRoles = custom => {
  const values = {isManager: false,
    isConsultant: false,
    isInviting: false,
    isBackOffice: false,
    isExplorer: false,
    hasPermission: false,
    currentRole: MANAGER_ROLE,
    ...custom};
  const roles = {
    isManager: () => values.isManager,
    isConsultant: () => values.isConsultant,
    isInviting: () => values.isInviting,
    hasPermission: () => values.hasPermission,
    isBackOffice: () => values.isBackOffice,
    isExplorer: () => values.isExplorer,
    getCurrentOrganisationRole: () => values.currentRole
  };
  jest.spyOn(authenticationService, 'roles').mockImplementation(() => roles);
  jest.spyOn(authenticationService, 'isManager').mockImplementation(() => roles.isManager());
  jest.spyOn(authenticationService, 'isInviting').mockImplementation(() => roles.isInviting());
  jest.spyOn(authenticationService, 'isConsultant').mockImplementation(() => roles.isConsultant());
  jest.spyOn(authenticationService, 'isBackOffice').mockImplementation(() => roles.isBackOffice());
  jest.spyOn(authenticationService, 'isExplorer').mockImplementation(() => roles.isExplorer());
  jest.spyOn(authenticationService, 'getCurrentOrganisationRole').mockImplementation(() => roles.getCurrentOrganisationRole());
};

export const authenticateAsManager = custom => mockUserRoles({isManager: true, ...custom});
export const authenticateAsConsultant = custom => mockUserRoles(
  {isConsultant: true, currentRole: CONSULTANT_ROLE, ...custom}
);
export const authenticateAsInviting = custom => mockUserRoles(
  {isInviting: true, currentRole: INVITING_ROLE, ...custom}
);
export const authenticateAsBackOffice = custom => mockUserRoles(
  {isBackOffice: true, currentRole: BACKOFFICE_ROLE, ...custom}
);
export const authenticateAsExplorer = custom => mockUserRoles(
  {isExplorer: true, currentRole: EXPLORER_ROLE, ...custom}
);

// ---------------------------- ACTIONS ----------------------------

export const allActions = () => ({
  setLocale: jest.fn(),
  setUser: jest.fn(),
  selectOrganisation: jest.fn(),
  addSuccess: jest.fn(),
  addError: jest.fn(),
  dismiss: jest.fn(),
  dismissAll: jest.fn(),
  // @TODO ce returnValue risque d'être pénible...
  fetchNotifications: jest.fn().mockResolvedValue(anHttpOk()),
  dismissNotification: jest.fn(),
  setNotificationViewed: jest.fn(),
  updateLastSeenNotifications: jest.fn(),
  setNewSubscription: jest.fn()
});

// ---------------------------- STORES ----------------------------

export const aClientsStore = (custom, dispatch = jest.fn()) => () => ({
  state: {
    depositor: {
      isActive: true,
      loading: false
    },
    clients: {
      list: [{
        ...anOrganisation(),
        favorite: false,
        relationshipId: 12
      }],
      loading: false,
      error: false,
      totalCount: 1,
      dossierCountry: COUNTRY.FRANCE,
      pageSize: 10
    },
    sentInvitations: {
      list: [anInvitation()],
      loading: false,
      error: false
    },
    ...custom
  },
  dispatch
});

export const aSubcontractorsStore = (custom, dispatch = jest.fn()) => () => ({
  state: {
    subcontractors: {
      list: [{
        ...anOrganisation(),
        favorite: false,
        relationshipId: 12
      }],
      loading: false,
      error: false,
      totalCount: 1,
      dossierCountry: COUNTRY.FRANCE,
      pageSize: 10
    },
    sentInvitations: {
      list: [anInvitation()],
      loading: false,
      error: false
    },
    ...custom
  },
  dispatch
});

export const aUsersStore = (custom, dispatch = jest.fn()) => () => ({
  state: {
    loading: true,
    error: false,
    users: [aUser()],
    totalCount: 1,
    pageSize: 25,
    ...custom
  },
  dispatch
});

export const aGlobalSubcontractorsStore = (custom, dispatch = jest.fn()) => () => ({
  state: {
    loading: false,
    error: false,
    subcontractors: [anOrganisation()],
    totalCount: 1,
    pageSize: 10,
    ...custom
  },
  dispatch
});

export const aClientDetailsStore = (custom, dispatch = jest.fn()) => () => ({
  state: {
    tabItems: getTabItems(),
    client: {
      details: anOrganisation({organisationId: 10}),
      loading: false,
      error: false
    },
    documents: {
      list: [aDocument()],
      loading: false,
      error: false
    },
    dossierState: {
      dossierState: DOSSIER_STATE.EMPTY,
      invalidDocuments: [],
      almostExpiredDocuments: [],
      loading: true,
      error: false
    },
    interventions: {
      list: [anIntervention()],
      loading: false,
      error: false,
      totalCount: 1,
      pageSize: 10
    },
    users: {
      list: [aUser()],
      loading: false,
      error: false,
      totalCount: 1,
      pageSize: 25
    },
    ...custom
  },
  dispatch
});

export const aSubcontractorDetailsStore = (custom, dispatch = jest.fn()) => () => ({
  state: {
    dossierCountry: COUNTRY.FRANCE,
    subcontractor: {
      details: anOrganisation({organisationId: 10}),
      loading: false,
      error: false
    },
    documents: {
      list: [aDocument()],
      loading: false,
      error: false
    },
    archives: {
      list: [aDocument()],
      loading: false,
      error: false
    },
    interventions: {
      list: [anIntervention()],
      loading: false,
      error: false,
      totalCount: 1,
      pageSize: 10
    },
    users: {
      list: [aUser()],
      loading: false,
      error: false,
      totalCount: 1,
      pageSize: 25
    },
    reminders: {
      list: [aReminder()],
      loading: false,
      error: false,
      totalCount: 1,
      pageSize: 10
    },
    ...custom
  },
  dispatch
});

export const aProjectsStore = (custom, dispatch = jest.fn()) => () => ({
  state: {
    loading: false,
    error: false,
    projects: [
      aProjectWithOneIntervention(),
      aProjectWithMultipleInterventions()
    ],
    totalCount: 2,
    pageSize: 10,
    ...custom
  },
  dispatch
});

export const aDashboardCollectionSectionStore = (custom, dispatch = jest.fn()) => () => ({
  state: {
    users: {
      count: 0,
      loading: false,
      error: false
    },
    clients: {
      count: 0,
      loading: false,
      error: false
    },
    projects: {
      count: 0,
      loading: false,
      error: false
    },
    subcontractors: {
      count: {
        total: 0,
        valid: 0
      },
      loading: false,
      error: false
    },
    ...custom
  },
  dispatch
});

export const aDashboardDepositorSectionStore = (custom, dispatch = jest.fn()) => () => ({
  state: {
    loading: false,
    error: null,
    documents: [aDocument({type: aDocumentType({country: COUNTRY.FRANCE})})],
    ...custom
  },
  dispatch
});
